import * as React from 'react';
import {
  AddExternshipApproverPayload,
  ApproverRole,
  ExternshipApproverEntity,
  ExternshipWithoutTimesheets,
  StudentViewExternship
} from '@api/models/externshipApi.models';
import {
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { Controller, FormProvider, useForm, ValidateResult } from 'react-hook-form';
import BasicModal from '@components/BasicModal';
import { emailPattern } from '@common/regex/email';
import { ExternshipApi } from '@api/Externships.api';
import FormFieldErrorText from '@components/Forms/FormFieldErrorText';
import ReactHookFormsAutoCompleteSingle from '@components/Forms/ReactHookFormsAutoCompleteSingle';
import ReactHookFormsInput from '@components/Forms/ReactHookFormsInput';
import ReactHookFormsInputPhoneMask from '@components/Forms/ReactHookFormsInputPhoneMask';
import { useStudentViewContext } from '@common/context/studentViewContext';
import { useToast } from '@components/Toast';

interface Props {
  open: boolean;
  closeModal: () => void;
  externship: ExternshipWithoutTimesheets;
}

const AddApprover: React.FC<Props> = ({ open, externship, closeModal }) => {
  const [searchValue, setSearchValue] = React.useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = React.useState('');
  const [approversFromSearch, setApproversFromSearch] = React.useState<ExternshipApproverEntity[]>([]);
  const [approverFromSearchSelected, setApproverFromSearchSelected] = React.useState<ExternshipApproverEntity | null>();
  const [searchLoading, setSearchLoading] = React.useState(false);

  const { setExternships } = useStudentViewContext();

  const debouncedTimeout = 500;
  const searchLoafingThreshold = 700;

  const defaultValues: AddExternshipApproverPayload = {
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    jobTitle: '',
    approverRole: ApproverRole.SUPERVISOR,
  };

  const methods = useForm<AddExternshipApproverPayload>({
    defaultValues, mode: 'onChange'
  });

  const {
    watch,
    setValue,
    clearErrors,
  } = methods;

  const [disableActions, setDisableActions] = React.useState(false);
  const [errorMsg, setErrMsg] = React.useState('');

  const emailInput = watch('email');
  const { openToast } = useToast();

  const manager = externship.approvers?.find(link => link.approverRole === ApproverRole.MANAGER);

  // Employers list for the autocomplete dropdown
  const approversFromSearchOptions = React.useMemo(() => {
    if (!approversFromSearch) return []; // Return an empty array if employers is undefined
    return approversFromSearch.map((approver: ExternshipApproverEntity) => ({
      label: `${approver.firstName || approver.user?.firstName} ${approver.lastName || approver.user?.lastName} - ${approver.invite?.email || approver.user?.email}`,
      value: approver.invite?.email || approver.user?.email
    }));
  }, [approversFromSearch]);

  // Setting form fields if approver is existing employer
  React.useEffect(() => {
    if (emailInput && emailInput !== '') {
      const approverFound = approversFromSearch.find((approver: ExternshipApproverEntity) => {
        return approver.invite?.email === emailInput?.trim().toLowerCase() || approver.user?.email === emailInput?.trim().toLowerCase();
      });

      if (approverFound) {
        clearErrors();
        setApproverFromSearchSelected(approverFound);

      } else {
        setApproverFromSearchSelected(null);
      }

      setValue('firstName', approverFound?.firstName || '');
      setValue('lastName', approverFound?.lastName || '');
      setValue('jobTitle', approverFound?.jobTitle || '');
      setValue('phone', approverFound?.phone || '');
    }

  }, [emailInput, approverFromSearchSelected, setApproverFromSearchSelected]);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, debouncedTimeout); // Adjust the delay as needed

    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  const sleep = (duration: number): Promise<void> => {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, duration);
    });
  };

  React.useEffect(() => {
    if (debouncedSearchValue) {
      const start = new Date().getTime();

      setSearchLoading(true);
      setApproversFromSearch([]);

      const antiFlicker = async (res: any):Promise<any> =>  {
        // This function ass artificial delay to the response rendering to prevent results flickering
        const stop = new Date().getTime();
        const delta = stop - start;
        if (delta < searchLoafingThreshold) {
          await sleep(searchLoafingThreshold - delta);
        }
        return res;
      };

      // Perform search or other actions
      ExternshipApi.searchPotentialApproverByEmail(externship.id, debouncedSearchValue).then(antiFlicker).then((res) => {
        setApproversFromSearch(res);
      }).catch(antiFlicker).catch((error) => {
        console.error('ExternshipApi.searchPotentialApproverByEmail', error);
        setApproversFromSearch([]);
      }).finally(() => {
        setSearchLoading(false);
      });
    }
  }, [debouncedSearchValue]);

  const clearModal = (): void => {
    setErrMsg('');
    closeModal();
  };

  const onValid = async (data: AddExternshipApproverPayload): Promise<void> => {
    setDisableActions(true);
    setErrMsg('');

    try {
      const res = await ExternshipApi.addExternshipApprover(externship.id.toString(), data);

      const mapUpdatedExternship = (
        e: StudentViewExternship
      ): StudentViewExternship => {
        if (e.id === res.id) {
          return {
            ...e,
            status: res.status,
            approvers: res.approvers,
          };
        }
        return { ...e };
      };

      setExternships((prev) => prev.map(mapUpdatedExternship));
      openToast('Approver added successfully.');
      closeModal();

    } catch (error: any) {
      console.error('Error: AddApprover -> ExternshipApi.addExternshipApprover()');
      if (error?.response?.data?.message) {
        setErrMsg(error?.response?.data?.message);
      }
    } finally {
      setDisableActions(false);
      setApproverFromSearchSelected(null);
    }
  };

  const onSearchInputChange = (
    _: React.SyntheticEvent,
    value: string,
  ): void => {
    setSearchValue(value);
  };

  return (
    <BasicModal
      open={open}
      handleClose={clearModal}
      title="Add an Approver"
      primaryAction={{
        label: 'Save',
        action: methods.handleSubmit(onValid),
        disabled: disableActions
      }}
      secondaryAction={{
        label: 'Cancel',
        action: clearModal,
        disabled: disableActions
      }}
      maxWidth="sm"
    >
      <FormProvider {...methods}>
        <Typography variant="EC_TYPE_BASE" mb={4}>
          Add an existing user or enter an email to send an invite to join
          Escoffier.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ReactHookFormsAutoCompleteSingle
              labelVariant="EC_TYPE_SM"
              name="email"
              openOnFocus={false}
              disablePortal
              freeSolo
              selectOnFocus
              blurOnSelect
              autoFocus={true}
              options={approversFromSearchOptions}
              placeholder="Search Approver or type a email"
              loading={searchLoading}
              onInputChange={onSearchInputChange}
              rules={{
                validate: (value: string): ValidateResult => {
                  // Equal to `required`
                  if (!value) return 'Email is required';

                  // Equal to `pattern`
                  if (!emailPattern.test(value)) return 'Please enter a valid email';

                  // Custom validator
                  externship.approvers.find(approver => {
                    if (
                      approver.user?.email === value.toLowerCase() ||
                      approver.invite?.email === value.toLowerCase()
                    ) {
                      return `Email is already assigned to the externship's ${approver.approverRole}`;
                    }
                  });

                  return true;
                },
              }}
            />
          </Grid>
          <React.Fragment>
            <Grid item xs='auto' alignContent={'center'}>
              <FormLabel id="add-approver-is-manager-radio-buttons-group">Approver type</FormLabel>
            </Grid>
            <Grid item xs='auto' textAlign={'left'}>
              <Controller
                name='approverRole'
                control={methods.control}
                render={({ field }): JSX.Element => {
                  return (
                    <RadioGroup
                      {...field}
                      row
                      aria-labelledby="add-approver-is-manager-radio-buttons-group"
                    >
                      <FormControlLabel
                        label="Manager"
                        value={ApproverRole.MANAGER}
                        disabled={!!manager}
                        control={<Radio />}
                      />
                      <FormControlLabel
                        label="Supervisor"
                        value={ApproverRole.SUPERVISOR}
                        control={<Radio />}
                      />
                      <FormControlLabel
                        label="Mentor"
                        value={ApproverRole.MENTOR}
                        control={<Radio />}
                      />
                    </RadioGroup>
                  );
                }}
              />
            </Grid>
          </React.Fragment>
          <React.Fragment>
            <Grid item xs={12} sm={6}>
              <ReactHookFormsInput
                dataTestId="add-approver-first-name-input"
                placeholder="First Name"
                name="firstName"
                rules={{
                  required: 'First Name is required'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReactHookFormsInput
                dataTestId="add-approver-last-name-input"
                placeholder="Last Name"
                name="lastName"
                rules={{
                  required: 'Last Name is required'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReactHookFormsInputPhoneMask
                name="phone"
                placeholder="Phone Number"
                dataTestId="add-approver-personal-phone-number-input"
                rules={{
                  required: 'Phone number is required'
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReactHookFormsInput
                dataTestId="add-approver-job-title-input"
                placeholder="Job Title"
                name="jobTitle"
                rules={{
                  required: 'Job title is required'
                }}
              />
            </Grid>
          </React.Fragment>
          {errorMsg && (
            <FormFieldErrorText
              message={errorMsg}
              disableAbsolutePosition
              name={`add-approver-${externship.id}`}
            />
          )}
        </Grid>
      </FormProvider>
    </BasicModal>
  );
};

export default AddApprover;
